import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, Link } from "react-router-dom";
import { 
  FileText, Receipt, FileCheck, Heart, CreditCard, 
  Wallet, FolderOpen, Settings, 
  ChevronLeft, ChevronRight, Sparkles, Home,
  FileUp, Wand2, TrendingUp, BookOpen, Users, LogOut, AlertCircle,
  LayoutDashboard, Scale, FileSpreadsheet, Menu, X, Package, BarChart3, ClipboardList, Banknote,
  ShoppingCart, Shield, Search
} from "lucide-react";
import { cn } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import axios from "axios";
 

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const documentTypes = [
  { type: "quote", label: "Quote", icon: FileCheck },
  { type: "payment-invoice", label: "Payment Invoice", icon: Banknote },
  { type: "receipt", label: "Receipt", icon: Receipt },
];

const converterTools = [
  { type: "word-to-pdf", label: "Word to PDF", icon: FileUp },
];

const aiTools = [
  { type: "ai-tools", label: "AI Assistant", icon: Wand2 },
];

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const loadBusinessProfile = async () => {
      try {
        const response = await axios.get(`${API}/business-profile`);
        setBusinessProfile(response.data);
      } catch (error) {
        console.error("Failed to load business profile:", error);
      }
    };

    const loadUserPermissions = async () => {
      try {
        const response = await axios.get(`${API}/auth/me/permissions`);
        setUserPermissions(response.data.permissions || []);
      } catch (error) {
        // Default to full access for account owners
        setUserPermissions(["create_documents", "view_documents", "manage_clients", "view_financials", "access_settings", "manage_inventory", "quick_sale"]);
      }
    };

    loadBusinessProfile();
    loadUserPermissions();
  }, []);

  // Permission helpers
  const canAccessSettings = userPermissions.includes("access_settings");
  const canViewFinancials = userPermissions.includes("view_financials");

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  // Only show onboarding reminder for account OWNERS (not team members)
  // Team members have company_id set, owners do not
  const isTeamMember = user?.company_id != null;
  const showOnboardingReminder = !isTeamMember && businessProfile && !businessProfile.onboarding_completed;

  return (
    <div className="min-h-screen bg-[#F7FAF9] flex">
      {/* Onboarding Reminder Banner */}
      {showOnboardingReminder && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-50 border-b border-amber-100 px-4 py-2.5">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs sm:text-sm font-medium">Complete your setup to add company logo</span>
            </div>
            <Link to="/onboarding">
              <Button size="sm" className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-4 shadow-sm">
                Complete Setup
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Mobile Header - Premium Design */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-100 h-16 flex items-center justify-between px-4 shadow-soft">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <span className="text-white font-heading font-bold text-lg">L</span>
          </div>
          <span className="font-heading font-semibold text-slate-800">LedgerAMT</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-10 h-10 flex items-center justify-center text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Premium Design */}
      <aside 
        className={cn(
          "bg-white border-r border-slate-100 fixed left-0 h-screen z-40 transition-all duration-300 flex flex-col shadow-soft",
          "lg:translate-x-0",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full",
          collapsed ? "lg:w-[72px]" : "lg:w-64",
          "w-72",
          showOnboardingReminder ? "top-10 lg:top-10" : "top-16 lg:top-0",
          "lg:top-0"
        )}
        style={{ top: showOnboardingReminder ? (mobileMenuOpen ? '40px' : undefined) : (mobileMenuOpen ? '64px' : undefined) }}
        data-testid="sidebar"
      >
        {/* Logo Section - Desktop */}
        <div className="hidden lg:flex p-5 border-b border-slate-100 items-center justify-between">
          {!collapsed ? (
            <div className="flex items-center">
              <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-12 w-auto" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center mx-auto shadow-primary">
              <span className="text-white font-heading font-bold text-xl">L</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {/* Dashboard - Primary */}
          <div className="mb-6">
            <NavLink
              to="/dashboard"
              end
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                isActive 
                  ? "bg-primary-50 text-primary font-semibold shadow-sm" 
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              )}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="font-medium">Dashboard</span>}
            </NavLink>
          </div>

          {/* Cash Flow Section - Only for users with view_financials permission */}
          {canViewFinancials && (
            <div className="mb-6">
              {!collapsed && (
                <div className="px-3 mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Cash Flow
                  </span>
                </div>
              )}
              
              <div className="space-y-1">
                <NavLink
                  to="/dashboard/cash-flow/ledger"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-cash-ledger"
                >
                  <BookOpen className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Cash Ledger</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/accounts-receivable"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-accounts-receivable"
                >
                  <FileText className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Accounts Receivable</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/cash-flow"
                  end
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-cash-flow"
                >
                  <TrendingUp className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Cash Flow</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/cash-flow-statement"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-cash-flow-statement"
                >
                  <FileSpreadsheet className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Cash Flow Statement</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/cash-flow/income-statement"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-income-statement"
                >
                  <FileText className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Income Statement</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/cash-flow/balance-sheet"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-balance-sheet"
                >
                  <Scale className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Balance Sheet</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/reports/analytics"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-data-analytics"
                >
                  <BarChart3 className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Data Analytics</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/reports/unified"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-unified-accounting"
                >
                  <ClipboardList className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Unified Accounting</span>}
                </NavLink>
                <NavLink
                  to="/dashboard/audit-trail"
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid="nav-audit-trail"
                >
                  <Shield className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">Audit Trail</span>}
                </NavLink>
              </div>
            </div>
          )}

          {/* Create Documents Section */}
          <div className="mb-6">
            {!collapsed && (
              <div className="px-3 mb-3">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Documents
                </span>
              </div>
            )}
            
            <div className="space-y-1">
              {documentTypes.map(({ type, label, icon: Icon }) => (
                <NavLink
                  key={type}
                  to={`/dashboard/tools/${type}`}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid={`nav-${type}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">{label}</span>}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Tools Section */}
          <div className="mb-6">
            {!collapsed && (
              <div className="px-3 mb-3">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Tools
                </span>
              </div>
            )}
            
            <div className="space-y-1">
              <NavLink
                to="/dashboard/quick-sale"
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                  isActive 
                    ? "bg-primary text-white font-semibold shadow-primary" 
                    : "bg-primary-50 text-primary hover:bg-primary-100"
                )}
                data-testid="nav-quick-sale"
              >
                <ShoppingCart className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="text-sm font-medium">Quick Sale</span>}
              </NavLink>
              <NavLink
                to="/dashboard/inventory"
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                  isActive 
                    ? "bg-primary-50 text-primary font-semibold" 
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
                data-testid="nav-inventory"
              >
                <Package className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="text-sm">Inventory</span>}
              </NavLink>
              <NavLink
                to="/dashboard/clients"
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                  isActive 
                    ? "bg-primary-50 text-primary font-semibold" 
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
                data-testid="nav-clients"
              >
                <Users className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="text-sm">Clients</span>}
              </NavLink>
              <NavLink
                to="/dashboard/library"
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                  isActive 
                    ? "bg-primary-50 text-primary font-semibold" 
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
                data-testid="nav-library"
              >
                <FolderOpen className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="text-sm">Library</span>}
              </NavLink>
              {aiTools.map(({ type, label, icon: Icon }) => (
                <NavLink
                  key={type}
                  to={`/dashboard/${type}`}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid={`nav-${type}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">{label}</span>}
                </NavLink>
              ))}
              {converterTools.map(({ type, label, icon: Icon }) => (
                <NavLink
                  key={type}
                  to={`/dashboard/tools/${type}`}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary-50 text-primary font-semibold" 
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                  data-testid={`nav-${type}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="text-sm">{label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        {/* Bottom Section */}
        <div className="mt-auto border-t border-slate-100 p-3">
          {canAccessSettings && (
            <NavLink
              to="/dashboard/settings"
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                isActive 
                  ? "bg-primary-50 text-primary font-semibold" 
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              )}
              data-testid="nav-settings"
            >
              <Settings className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="text-sm">Settings</span>}
            </NavLink>
          )}
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-slate-500 hover:bg-red-50 hover:text-red-600 w-full mt-1"
            data-testid="nav-logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
            {!collapsed && <span className="text-sm">Sign Out</span>}
          </button>
        </div>

        {/* Collapse Toggle - Desktop only */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-24 w-6 h-6 bg-white border border-slate-200 rounded-full shadow-sm items-center justify-center text-slate-400 hover:text-primary hover:border-primary transition-colors"
          data-testid="collapse-sidebar"
        >
          {collapsed ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5" />
          )}
        </button>
      </aside>

      {/* Main Content */}
      <main 
        className={cn(
          "flex-1 transition-all duration-300 w-full bg-[#F7FAF9]",
          "ml-0 pt-16",
          collapsed ? "lg:ml-[72px]" : "lg:ml-64",
          "lg:pt-0",
          showOnboardingReminder ? "pt-26 lg:pt-10" : ""
        )}
      >
        <div className="p-4 sm:p-8 lg:p-10 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
